import { useReducedMotion } from 'framer-motion';
import {
  Children,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ComponentType,
  type CSSProperties,
  type FC,
  type ReactNode,
} from 'react';
import { useLocation } from 'react-router-dom';
import { ActionModeProvider } from 'src/components/action-mode';
import { Button } from 'src/components/buttons-and-actions/button';
import { isTransportActive } from 'src/components/layout/app-shell/is-transport-active';
import { sidebarTransitionDuration } from 'src/components/layout/app-shell/sidebar';
import type { LoadedAppShellData } from 'src/components/layout/app-shell/use-app-shell-data';
import { useNavigationItems } from 'src/components/layout/app-shell/use-navigation-items';
import { isReportingActive } from 'src/components/layout/header/secondary-nav-reporting';
import type { IconProps } from 'src/components/media-and-icons/icon';
import {
  ArrowLeftRightIcon,
  BellRingIcon,
  BuildingIcon,
  ChartNoAxesCombinedIcon,
  ChevronDownIcon,
  CircleQuestionMarkIcon,
  CylinderIcon,
  FileChartColumnIcon,
  InfoIcon,
  LayoutDashboardIcon,
  MapPinnedIcon,
  MarketMessagesIcon,
  ShipIcon,
  ShippersIcon,
  UploadIcon,
} from 'src/components/media-and-icons/icons';
import { Link, ExternalLink } from 'src/components/navigation/link';
import { Tooltip } from 'src/components/overlay/tooltip';
import { cn } from 'src/utils/cn';

type NavigationProps = {
  appShellData: LoadedAppShellData;
  isSidebarExpanded: boolean;
  isSidebarTransitioning: boolean;
  expandSidebarForInteraction: () => void;
};

export const Navigation: FC<NavigationProps> = ({
  appShellData,
  isSidebarExpanded,
  isSidebarTransitioning,
  expandSidebarForInteraction,
}) => {
  const navigationItems = useNavigationItems(appShellData);
  const { pathname, hash } = useLocation();
  const showTooltip = !isSidebarExpanded && !isSidebarTransitioning;

  return (
    <nav
      className={cn(
        `
          relative z-0 -mt-(--sidebar-padding) -mb-(--sidebar-padding)
          flex-grow-1 overflow-x-hidden overflow-y-auto
          bg-background-sidebar-default p-(--sidebar-padding)
        `,
        `
          before:pointer-events-none before:sticky
          before:-top-(--sidebar-padding) before:z-1
          before:-mt-(--sidebar-padding) before:block
          before:h-(--sidebar-padding) before:bg-linear-to-b
          before:from-background-sidebar-default before:to-transparent
        `,
        `
          after:pointer-events-none after:sticky
          after:-bottom-(--sidebar-padding) after:z-1
          after:-mb-(--sidebar-padding) after:block after:h-(--sidebar-padding)
          after:bg-linear-to-t after:from-background-sidebar-default
          after:to-transparent
        `,
        // When sidebar is collapsed, we calculate the padding based on the sidebar collapsed width minus the min-width of the menu items (to let the icon visible)
        `
          group-data-[sidebar-expanded=false]:px-[calc(calc(var(--sidebar-width-collapsed)-var(--spacing-3xl))/2)]
        `
      )}
    >
      <ul
        className={cn(
          `
            grid grid-cols-1 gap-xs
            group-data-[sidebar-expanded=true]:gap-xl
          `
        )}
      >
        <NavigationGroup>
          {navigationItems.topNav.guestDashboard && (
            <Tooltip content={showTooltip ? 'Dashboard' : undefined} noTabIndex>
              {(targetProps) => (
                <Link
                  to="/"
                  icon={LayoutDashboardIcon}
                  isActive={pathname === '/'}
                  {...targetProps}
                >
                  Dashboard
                </Link>
              )}
            </Tooltip>
          )}

          {navigationItems.topNav.commonDashboard && (
            <Tooltip content={showTooltip ? 'Dashboard' : undefined} noTabIndex>
              {(targetProps) => (
                <Link
                  mode="navigation-item"
                  to="/dashboard"
                  icon={LayoutDashboardIcon}
                  {...targetProps}
                >
                  Dashboard
                </Link>
              )}
            </Tooltip>
          )}

          {navigationItems.topNav.shipperOrTsoDashboard && (
            <Tooltip content={showTooltip ? 'Dashboard' : null} noTabIndex>
              {(targetProps) => (
                <Link
                  to="/platform/#/start"
                  icon={LayoutDashboardIcon}
                  isActive={
                    pathname.startsWith('/platform') &&
                    hash.startsWith('#/start')
                  }
                  {...targetProps}
                >
                  Dashboard
                </Link>
              )}
            </Tooltip>
          )}

          {navigationItems.topNav.activity && (
            <Tooltip content={showTooltip ? 'Activity' : undefined} noTabIndex>
              {(targetProps) => (
                <Link
                  to="/platform/#/activity"
                  icon={BellRingIcon}
                  isActive={
                    pathname.startsWith('/platform') &&
                    hash.startsWith('#/activity')
                  }
                  {...targetProps}
                >
                  Activity
                </Link>
              )}
            </Tooltip>
          )}

          {navigationItems.topNav.notifications && (
            <Tooltip
              content={showTooltip ? 'Notifications' : undefined}
              noTabIndex
            >
              {(targetProps) => (
                <Link
                  to="/platform/#/notifications"
                  icon={BellRingIcon}
                  isActive={
                    pathname.startsWith('/platform') &&
                    hash.startsWith('#/notifications')
                  }
                  {...targetProps}
                >
                  Notifications
                </Link>
              )}
            </Tooltip>
          )}

          {!emptyGroup(navigationItems.topNav.marketMessages) && (
            <NavigationChildGroup
              label="Market Messages"
              icon={MarketMessagesIcon}
              isActive={pathname.startsWith('/market-messages')}
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
            >
              {navigationItems.topNav.marketMessages.urgentMarketMessages && (
                <Link to="/market-messages/urgent-market-messages">
                  Urgent Market Messages
                </Link>
              )}

              {navigationItems.topNav.marketMessages.maintenanceMessages && (
                <Link to="/market-messages/maintenance-messages">
                  Maintenance Messages
                </Link>
              )}
            </NavigationChildGroup>
          )}

          {!emptyGroup(navigationItems.topNav.shippers) && (
            <NavigationChildGroup
              label="Shippers"
              icon={ShippersIcon}
              isActive={pathname.startsWith('/shippers')}
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
            >
              {navigationItems.topNav.shippers.organisationsAndUsers && (
                <Link
                  to="/shippers/organisations-and-users"
                  isActive={
                    pathname.startsWith('/shippers/organisations-and-users') ||
                    pathname.startsWith('/dashboard/shippers')
                  }
                >
                  Organisations and Users
                </Link>
              )}

              {navigationItems.topNav.shippers.documents && (
                <Link
                  to="/shippers/documents"
                  isActive={
                    pathname.startsWith('/shippers/documents') ||
                    pathname.startsWith('/shippers/upload-documents')
                  }
                >
                  Documents
                </Link>
              )}

              {navigationItems.topNav.shippers.invoices && (
                <Link to="/shippers/invoices">Invoices</Link>
              )}

              {navigationItems.topNav.shippers.creditLimits && (
                <Link to="/shippers/credit-limit">Credit Limits</Link>
              )}
            </NavigationChildGroup>
          )}

          {!emptyGroup(navigationItems.topNav.operators) && (
            <NavigationChildGroup
              label="Operators"
              icon={BuildingIcon}
              isActive={
                pathname.startsWith('/operators') ||
                pathname.startsWith('/dashboard/operators')
              }
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
              data-help-annotation="assignments:2"
            >
              {navigationItems.topNav.operators.assignments && (
                <Link
                  to="/operators/assignments"
                  isActive={
                    pathname.startsWith('/operators/assignments') ||
                    pathname.startsWith('/dashboard/operators/assignments')
                  }
                >
                  Assignments
                </Link>
              )}

              {navigationItems.topNav.operators.documents && (
                <Link to="/operators/operator-documents">Documents</Link>
              )}

              {navigationItems.topNav.operators.invoices && (
                <Link to="/operators/invoices">Invoices</Link>
              )}
            </NavigationChildGroup>
          )}

          {navigationItems.topNav.uploads && (
            <Tooltip content={showTooltip ? 'Uploads' : undefined} noTabIndex>
              {(targetProps) => (
                <Link
                  to="/platform/#/uploads"
                  icon={UploadIcon}
                  isActive={
                    pathname.startsWith('/platform') &&
                    hash.startsWith('#/uploads')
                  }
                  {...targetProps}
                >
                  Uploads
                </Link>
              )}
            </Tooltip>
          )}
        </NavigationGroup>

        <NavigationGroup label="Gas Capacity">
          {!emptyGroup(navigationItems.transport) && (
            <NavigationChildGroup
              label="Transport"
              icon={ArrowLeftRightIcon}
              isActive={isTransportActive(pathname, hash)}
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
              data-help-annotation="transport:1"
            >
              {navigationItems.transport.products && (
                <Link to="/transport/products">Products</Link>
              )}

              {navigationItems.transport.auctions && (
                <Link
                  to="/transport/auctions"
                  isActive={
                    (pathname.startsWith('/platform') &&
                      hash.startsWith('#/auctions')) ||
                    pathname.startsWith('/transport/auctions') ||
                    pathname.startsWith('/snam-bidding') ||
                    pathname.startsWith('/modulation-service-auctions')
                  }
                >
                  Auctions
                </Link>
              )}

              {navigationItems.transport.manageDirectFcfs && (
                <Link to="/transport/manage-direct-fcfs">
                  Manage Direct FCFS
                </Link>
              )}

              {navigationItems.transport.fcfsBooking && (
                <Link to="/transport/fcfs-booking">FCFS Booking</Link>
              )}

              {navigationItems.transport.secondaryTrading && (
                <Link
                  to="/transport/secondary"
                  isActive={
                    (pathname.startsWith('/platform') &&
                      (hash.startsWith('#/create-external-trade') ||
                        hash.startsWith('#/edit-proposal') ||
                        hash.startsWith('#/create-proposal'))) ||
                    pathname.startsWith('/transport/secondary')
                  }
                >
                  Secondary Trading
                </Link>
              )}

              {navigationItems.transport.networkPoints && (
                <Link
                  to="/transport/network-points"
                  isActive={
                    (pathname.startsWith('/platform') &&
                      hash.startsWith('#/network-point') &&
                      !hash.startsWith('#/network-point/bundle')) ||
                    pathname.startsWith('/transport/network-points')
                  }
                >
                  Network Points
                </Link>
              )}

              {navigationItems.transport.bundleRequests && (
                <Link to="/transport/bundle-requests">Bundle Requests</Link>
              )}

              {navigationItems.transport.multiYearAuctions && (
                <Link
                  to="/platform/#/multi-year-auctions"
                  isActive={
                    pathname.startsWith('/platform') &&
                    hash.startsWith('#/multi-year-auctions')
                  }
                >
                  Multi-Year Auctions
                </Link>
              )}

              {navigationItems.transport.snamBidCollector && (
                <Link to="/transport/snam-bid-collector">Bid Collector</Link>
              )}
            </NavigationChildGroup>
          )}

          {emptyGroup(navigationItems.storage) ? null : onlyFirstItem(
              navigationItems.storage
            ) ? (
            <Tooltip content={showTooltip ? 'Storage' : undefined} noTabIndex>
              {(targetProps) => (
                <Link
                  to="/storage/offers"
                  icon={CylinderIcon}
                  isActive={
                    pathname.startsWith('/storage/offers') ||
                    (pathname.startsWith('/platform') &&
                      hash.startsWith('#/storage/offers'))
                  }
                  {...targetProps}
                >
                  Storage
                </Link>
              )}
            </Tooltip>
          ) : (
            <NavigationChildGroup
              label="Storage"
              icon={CylinderIcon}
              isActive={
                pathname.startsWith('/storage') ||
                (pathname.startsWith('/platform') &&
                  hash.startsWith('#/storage'))
              }
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
            >
              {navigationItems.storage.offers && (
                <Link
                  to="/storage/offers"
                  isActive={
                    pathname.startsWith('/storage/offers') ||
                    (pathname.startsWith('/platform') &&
                      hash.startsWith('#/storage/offers'))
                  }
                >
                  {appShellData.monolithUser?.isShipper
                    ? 'Auctions'
                    : 'Manage Offers'}
                </Link>
              )}

              {navigationItems.storage.fcfsBooking && (
                <Link
                  to="/platform/#/storage/booking"
                  isActive={
                    pathname.startsWith('/platform') &&
                    hash.startsWith('#/storage/booking')
                  }
                >
                  FCFS Booking
                </Link>
              )}

              {navigationItems.storage.locations && (
                <Link
                  to="/storage/locations"
                  isActive={
                    pathname.startsWith('/storage/locations') ||
                    (pathname.startsWith('/platform') &&
                      hash.startsWith('#/storage/locations'))
                  }
                >
                  Storage Locations
                </Link>
              )}
            </NavigationChildGroup>
          )}

          {onlyFirstItem(navigationItems.lng) ? (
            <Tooltip content={showTooltip ? 'LNG' : undefined} noTabIndex>
              {(targetProps) => (
                <Link to="/lng/offers" icon={ShipIcon} {...targetProps}>
                  LNG
                </Link>
              )}
            </Tooltip>
          ) : (
            <NavigationChildGroup
              label="LNG"
              icon={ShipIcon}
              isActive={pathname.startsWith('/lng')}
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
            >
              <Link to="/lng/offers">Offers</Link>

              {navigationItems.lng.allocations && (
                <Link to="/lng/allocations">Allocations</Link>
              )}
            </NavigationChildGroup>
          )}

          <NavigationChildGroup
            label="Reporting"
            icon={FileChartColumnIcon}
            isActive={isReportingActive(pathname, hash)}
            isSidebarExpanded={isSidebarExpanded}
            isSidebarTransitioning={isSidebarTransitioning}
            expandSidebarForInteraction={expandSidebarForInteraction}
          >
            {navigationItems.reporting.myTransactions && (
              <Link to="/reporting/my-transactions">My Transactions</Link>
            )}

            {navigationItems.reporting.standardReports && (
              <Link to="/reporting/standard">Standard Reports</Link>
            )}

            {navigationItems.reporting.auctions && (
              <Link to="/reporting/auctions">Auctions</Link>
            )}

            {navigationItems.reporting.secondaryTrading && (
              <Link to="/reporting/secondary">Secondary Trading</Link>
            )}

            {navigationItems.reporting.lng && (
              <Link to="/reporting/lng">LNG</Link>
            )}

            {navigationItems.reporting.shipperTransactions && (
              <Link to="/reporting/shipper-transactions">
                Shipper Transactions
              </Link>
            )}

            {navigationItems.reporting.monthlyStatements && (
              <Link to="/reporting/monthly-statements">Monthly Statements</Link>
            )}

            {navigationItems.reporting.balancingGroupAllocations && (
              <Link to="/reporting/balancing-group-allocations">
                Balancing Group Allocations
              </Link>
            )}

            {navigationItems.reporting.tr && (
              <Link to="/reporting/tr">Transparency Reporting</Link>
            )}

            {navigationItems.reporting.remitReports && (
              <Link to="/reporting/remit-reports">REMIT Reports</Link>
            )}

            {navigationItems.reporting.surrenders && (
              <Link to="/reporting/surrenders">Surrenders</Link>
            )}

            {navigationItems.reporting.contractPositions && (
              <Link to="/platform/#/reporting/contracts">
                Contract Positions
              </Link>
            )}
          </NavigationChildGroup>

          {!emptyGroup(navigationItems.gdm) && (
            <NavigationChildGroup
              label="GDM"
              icon={ChartNoAxesCombinedIcon}
              isActive={pathname.startsWith('/gdm')}
              isSidebarExpanded={isSidebarExpanded}
              isSidebarTransitioning={isSidebarTransitioning}
              expandSidebarForInteraction={expandSidebarForInteraction}
            >
              {navigationItems.gdm.timeSeries && (
                <Link to="/gdm/ts-master">Time Series</Link>
              )}

              {navigationItems.gdm.verifications && (
                <Link to="/gdm/ts-verification">Verifications</Link>
              )}

              {navigationItems.gdm.reports && (
                <Link to="/gdm/ts-report">Reports</Link>
              )}
            </NavigationChildGroup>
          )}

          {navigationItems.mapPrototype && (
            <Tooltip
              content={showTooltip ? '[Prototype] Map' : undefined}
              noTabIndex
            >
              {(targetProps) => (
                <Link to="/prototype-map" icon={MapPinnedIcon} {...targetProps}>
                  [Prototype] Map
                </Link>
              )}
            </Tooltip>
          )}
        </NavigationGroup>

        <NavigationGroup label="PRISMA">
          {navigationItems.prisma.helpAndSupportPortal && (
            <Tooltip
              content={showTooltip ? 'Help & Support Portal' : undefined}
              noTabIndex
            >
              {(targetProps) => (
                <ExternalLink
                  href="https://help.prisma-capacity.eu/"
                  target="_blank"
                  icon={CircleQuestionMarkIcon}
                  {...targetProps}
                >
                  Help & Support Portal
                </ExternalLink>
              )}
            </Tooltip>
          )}

          <NavigationChildGroup
            label="Market Information"
            icon={InfoIcon}
            isActive={pathname.startsWith('/market-information')}
            isSidebarExpanded={isSidebarExpanded}
            isSidebarTransitioning={isSidebarTransitioning}
            expandSidebarForInteraction={expandSidebarForInteraction}
          >
            {navigationItems.prisma.marketInformation && (
              <Link to="/market-information/players">Market Participants</Link>
            )}

            {navigationItems.prisma.exchangeRates && (
              <Link to="/market-information/rates">Exchange Rates</Link>
            )}
          </NavigationChildGroup>
        </NavigationGroup>
      </ul>
    </nav>
  );
};

const NavigationGroup: FC<{
  label?: ReactNode;
  children: ReactNode;
}> = ({ label, children }) => {
  const actionMode = useMemo(() => ({ mode: 'navigation-item' as const }), []);
  return (
    <li
      className="grid grid-cols-1 gap-xs"
      data-testid="sidebar-navigation-group"
    >
      {label && (
        <p
          className={`
            truncate px-md text-label-sm font-label-semibold
            text-foreground-sidebar-subdued
            group-data-[sidebar-expanded=false]:hidden
          `}
        >
          {label}
        </p>
      )}

      <ActionModeProvider value={actionMode}>
        <ul className="grid grid-cols-1 gap-xs">
          {Children.map(children, (child) =>
            child ? (
              <li data-testid="sidebar-navigation-item">{child}</li>
            ) : null
          )}
        </ul>
      </ActionModeProvider>
    </li>
  );
};

const NavigationChildGroup: FC<{
  children: ReactNode;
  label: string;
  icon: ComponentType<IconProps>;
  isActive: boolean;
  isSidebarExpanded: boolean;
  isSidebarTransitioning: boolean;
  expandSidebarForInteraction: () => void;
  'data-help-annotation'?: string;
}> = ({
  children,
  label,
  icon: IconComponent,
  isActive,
  isSidebarExpanded,
  isSidebarTransitioning,
  expandSidebarForInteraction,
  'data-help-annotation': dataHelpAnnotation,
}) => {
  const [isExpanded, setIsExpanded] = useState(isActive);

  const groupRef = useRef<HTMLDivElement>(null);
  const actionMode = useMemo(
    () => ({ mode: 'navigation-child-item' as const }),
    []
  );
  const showTooltip = !isSidebarExpanded && !isSidebarTransitioning;
  const reducedMotion = useReducedMotion();

  const scrollIntoView = (groupRef: React.RefObject<HTMLDivElement>) => {
    // For now we wait for the duration of the expand/collapse transition of the sidebar in order to scroll to the clicked item since its position changes after expanding the sidebar
    setTimeout(
      () => {
        requestAnimationFrame(() => {
          const element = groupRef.current;
          if (!element) return;

          element.scrollIntoView({
            block: 'center',
            behavior: reducedMotion ? 'instant' : 'smooth',
          });
        });
      },
      reducedMotion ? 0 : sidebarTransitionDuration
    );
  };

  useEffect(() => {
    // only trigger expansion of the navigation item, if the sidebar is expanded as well
    if (!isSidebarExpanded || isSidebarTransitioning) return;

    // whenever the navigation item is active, expand it and scroll it into view
    if (!isActive) return;
    setIsExpanded(true);

    scrollIntoView(groupRef);
    // oxlint-disable-next-line exhaustive-deps
  }, [isActive]);

  return (
    <div className={cn(`grid grid-cols-1`)} ref={groupRef}>
      <Tooltip content={showTooltip ? label : undefined} noTabIndex>
        {(targetProps) => (
          <Button
            mode="navigation-item"
            isActive={isActive}
            data-help-annotation={dataHelpAnnotation}
            icon={IconComponent}
            className={cn(
              isActive &&
                isExpanded &&
                isSidebarExpanded &&
                `bg-transparent text-foreground-sidebar-selected`
            )}
            onClick={() => {
              expandSidebarForInteraction();
              setIsExpanded(!isSidebarExpanded ? true : !isExpanded);
              if (!isSidebarExpanded) scrollIntoView(groupRef);
            }}
            aria-expanded={isExpanded}
            aria-label={`${isExpanded ? 'Collapse' : 'Expand'} ${label}`}
            {...targetProps}
          >
            {label}

            <div
              className={cn(`
                flex items-center gap-md
                group-data-[sidebar-expanded=false]:hidden
              `)}
            >
              <ChevronDownIcon
                size="sm"
                className={cn(
                  `text-foreground-sidebar-subdued`,
                  `transition-transform duration-200 ease-in-out`,
                  isExpanded && `rotate-180`
                )}
              />
            </div>
          </Button>
        )}
      </Tooltip>

      <div
        className={cn(
          `
            grid transition-[grid-template-rows]
            duration-(--transition-duration) ease-in-out
            will-change-[grid-template-rows]
            motion-reduce:transition-none
          `,
          isExpanded && isSidebarExpanded && `grid-rows-[1fr]`,
          (!isExpanded || !isSidebarExpanded) && `grid-rows-[0fr]`
        )}
        style={
          {
            '--transition-duration': `${sidebarTransitionDuration}ms`,
          } as CSSProperties
        }
      >
        <div className="overflow-hidden">
          <ActionModeProvider value={actionMode}>
            <ul className="mt-xs">
              {Children.map(children, (child) =>
                child ? (
                  <li data-testid="sidebar-navigation-child-item">{child}</li>
                ) : null
              )}
            </ul>
          </ActionModeProvider>
        </div>
      </div>
    </div>
  );
};

function emptyGroup(group: Record<string, unknown>) {
  return Object.values(group).every((value) => !value);
}

function onlyFirstItem(group: Record<string, unknown>) {
  return Object.values(group).every((value, index) =>
    index === 0 ? value : !value
  );
}
